import { Controller } from '@hotwired/stimulus'
import { disableScroll, enableScroll } from '../utils/global'

// Контроллер для всех модалок проекта
// Активирует модалку при connect() и освобождает скролл при disconnect()

// ID turbo-frame которые считаются модальными (см. layouts/application.html.erb)
const MODAL_FRAME_IDS = ['modal', 'adv-modal', 'drawer']

export default class ModalController extends Controller {
  static targets = ['content', 'closeButton']

  connect() {
    const frame = this.element.closest('turbo-frame')
    const inTurboFrame = !!frame && MODAL_FRAME_IDS.includes(frame.id)

    this._modalFrame = inTurboFrame ? frame : null
    this._boundOnContentScroll = this.#onContentScroll.bind(this)
    this.#observeContentScroll()

    if (inTurboFrame) {
      // Модалка загружена динамически через Turbo Frame — активируем автоматически
      this._scrollLocked = !this.isAdmin()

      // Активируем модалку с анимацией (двойной requestAnimationFrame для transition)
      if (!this.element.classList.contains('is-active')) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.element.classList.add('is-active')
          })
        })
      }

      // Блокируем скролл страницы для обычных модалок;
      // для модификатора `admin` (форма бронирования у сотрудников) скролл не блокируем
      if (this._scrollLocked) {
        disableScroll()
      }
      this.focusFirstInput()
    }
  }

  isAdmin() {
    return this.element.classList.contains('modal--admin')
  }

  disconnect() {
    this.#unobserveContentScroll()

    // Разблокируем скролл при удалении модалки из DOM
    if (this._scrollLocked) enableScroll()
  }

  close(event) {
    if (event) event.preventDefault()

    const scrollToId = event?.currentTarget?.dataset?.scrollTo

    this._releaseFocusFromModal()
    this.#unobserveContentScroll()

    if (this._modalFrame) {
      // Очищаем frame — иначе при следующем открытии Turbo не перезагрузит содержимое

      this.element.classList.remove('is-active')
      this.element.addEventListener(
        'transitionend',
        () => {
          this._modalFrame.innerHTML = ''
        },
        { once: true }
      )
      // this._modalFrame.innerHTML = ''
    } else {
      // если модалка окажется вне фрейма — это fallback
      this.element.classList.remove('is-active')
    }
    // Разблокируем скролл только если он был заблокирован этим инстансом
    if (this._scrollLocked) {
      enableScroll()
      this._scrollLocked = false
    }

    if (scrollToId) {
      requestAnimationFrame(() => {
        this.scrollToTarget(scrollToId)
      })
    }
  }

  // Legacy-метод для статических модалок из ui-компонента (component 'modal').
  // Такие модалки рендерятся в DOM сразу при загрузке страницы и открываются
  // явным вызовом: data-action="click->modal#open" на кнопке-триггере.
  //
  // Все остальные модалки в проекте динамические:
  //   1. В layout рендерится пустой <turbo-frame id="...">
  //   2. По триггеру (клик или таймер) фрейму устанавливается src
  //   3. Turbo загружает контент из Rails-экшна и вставляет его во фрейм
  //   4. Stimulus видит появление элемента с data-controller="modal" в DOM → connect()
  //   5. connect() определяет что элемент внутри turbo-frame → активирует + scroll-lock
  //   6. При закрытии frame.innerHTML очищается → disconnect() → scroll разблокируется
  open() {
    if (this.element.classList.contains('is-active')) return
    this._scrollLocked = !this.isAdmin()
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.element.classList.add('is-active')
        this.#observeContentScroll()
      })
    })
    if (this._scrollLocked) {
      disableScroll()
    }
    this.focusFirstInput()
  }

  closeOverlay(event) {
    if (event.target === event.currentTarget) this.close(event)
  }

  // Предотвращает уход фокуса с текущего поля при mousedown на кнопке закрытия,
  // чтобы blur-валидация не успела показать ошибки до того, как модалка закроется
  preventBlur(event) {
    event.preventDefault()
  }

  back(event) {
    if (event) event.preventDefault()
    history.back()
  }

  handleEscape(event) {
    if (event.key !== 'Escape') return
    if (!this.element.classList.contains('is-active')) return
    event.preventDefault()
    this.close()
  }
  // сначала ищет поля ввода, при их отсутствии — первую кнопку/ссылку
  focusFirstInput() {
    const field = this.element.querySelector(
      'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled])'
    )
    if (field) {
      field.focus()
      return
    }
    this.element
      .querySelector(
        'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )
      ?.focus()
  }

  scrollToTarget(id) {
    const target = document.getElementById(id)

    if (!target) return

    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Снимаем фокус до размонтирования модалки, иначе браузер переносит его
  // на следующий tabbable (часто в футере) и прокручивает страницу вниз
  _releaseFocusFromModal() {
    const active = document.activeElement

    if (!(active instanceof HTMLElement)) return
    if (!this.element.contains(active)) return

    active.blur()
  }

  #observeContentScroll() {
    if (!this.hasContentTarget) return

    this.contentTarget.addEventListener('scroll', this._boundOnContentScroll, {
      passive: true
    })
  }

  #unobserveContentScroll() {
    if (!this.hasContentTarget) return

    this.contentTarget.removeEventListener('scroll', this._boundOnContentScroll)
  }

  #onContentScroll() {
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.classList.toggle(
        'has-shadow',
        this.contentTarget.scrollTop > 0
      )
    }
  }
}
